import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { API_BASE_URL } from '../lib/api';

export default function HomePage() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(null);
  const [districtId, setDistrictId] = useState(0);
  const [talukaId, setTalukaId] = useState(0);
  const [villageId, setVillageId] = useState(0);
  const [query, setQuery] = useState('');
  const [propertyType, setPropertyType] = useState('land');

  useEffect(() => {
    fetch(`${API_BASE_URL}/locations`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.districts) return;
        setIndex(data);
        const d0 = data.districts.find((d) => d.en === 'Pune') || data.districts[0];
        if (!d0) return;
        setDistrictId(d0.id);
        const t0 = d0.talukas?.[0];
        if (t0) {
          setTalukaId(t0.id);
          setVillageId(t0.villages?.[0]?.id || 0);
        }
      })
      .catch(() => {});
  }, []);

  const district = index?.districts?.find((d) => d.id === districtId);
  const talukas = district?.talukas || [];
  const taluka = talukas.find((t) => t.id === talukaId);
  const villages = taluka?.villages || [];
  const village = villages.find((v) => v.id === villageId);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!district || !taluka || !village || !query) return;
    const p = new URLSearchParams({
      d: district.id,
      t: taluka.id,
      v: village.id,
      de: district.en,
      te: taluka.en,
      ve: village.en,
      query,
      propertyType,
    });
    navigate(`/results?${p}`);
  };

  return (
    <Shell narrow>
      <div className="text-center mb-8 animate-in">
        <span className="badge">Maharashtra · IGR</span>
        <h1 className="mt-4 text-2xl sm:text-[1.75rem] font-bold text-ink leading-tight">
          Find your{' '}
          <span className="bg-gradient-to-r from-brand to-accent bg-clip-text text-transparent">
            property records
          </span>
        </h1>
        <p className="text-muted text-sm mt-2 max-w-sm mx-auto">
          Search by location and plot number. Pay once, get the full report.
        </p>
        <div className="flex flex-wrap justify-center gap-2 mt-5">
          <span className="pill">Fast search</span>
          <span className="pill">Verified data</span>
          <span className="pill">Instant download</span>
        </div>
      </div>

      <form onSubmit={onSubmit} className="card-accent space-y-4 animate-in" style={{ animationDelay: '0.08s' }}>
        <div>
          <label className="label">District</label>
          <select
            className="input"
            value={districtId || ''}
            onChange={(e) => {
              const id = +e.target.value;
              setDistrictId(id);
              const d = index?.districts?.find((x) => x.id === id);
              const t = d?.talukas?.[0];
              setTalukaId(t?.id || 0);
              setVillageId(t?.villages?.[0]?.id || 0);
            }}
            required
          >
            <option value="">Select district</option>
            {(index?.districts || []).map((d) => (
              <option key={d.id} value={d.id}>{d.en}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Taluka</label>
            <select
              className="input"
              value={talukaId || ''}
              onChange={(e) => {
                const id = +e.target.value;
                setTalukaId(id);
                const t = talukas.find((x) => x.id === id);
                setVillageId(t?.villages?.[0]?.id || 0);
              }}
              disabled={!district}
              required
            >
              <option value="">Select</option>
              {talukas.map((t) => <option key={t.id} value={t.id}>{t.en}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Village</label>
            <select
              className="input"
              value={villageId || ''}
              onChange={(e) => setVillageId(+e.target.value)}
              disabled={!taluka}
              required
            >
              <option value="">Select</option>
              {villages.map((v) => <option key={v.id} value={v.id}>{v.en}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <label className="label">Type</label>
            <select className="input" value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
              <option value="land">Land</option>
              <option value="flat">Flat</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Property number</label>
            <input
              className="input font-semibold"
              placeholder="e.g. 42"
              value={query}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[0-9]+$/.test(v)) setQuery(v);
              }}
              required
            />
          </div>
        </div>

        <label className="flex items-start gap-2.5 text-xs text-muted cursor-pointer rounded-lg bg-wash/80 px-3 py-2.5">
          <input type="checkbox" required className="mt-0.5 rounded border-line text-brand focus:ring-brand" />
          I agree to use this service for lawful purposes.
        </label>

        <button type="submit" className="btn w-full">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
          </svg>
          Search records
        </button>
      </form>
    </Shell>
  );
}
